import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type PropsWithChildren, type ReactNode } from 'react';
import { BookOpen, X } from 'lucide-react';
import type { Book } from '../../domain/entities/types';
import { useCoverUrl } from '../../hooks/useData';
import { progressPercentage } from '../../utils/core';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' };

export const Button = ({ variant = 'primary', className = '', ...props }: ButtonProps) => (
  <button className={`button button--${variant} ${className}`} {...props} />
);

export const IconButton = ({ label, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) => (
  <button className="icon-button" aria-label={label} title={label} {...props} />
);

type ProgressBarProps = {
  value?: number;
  label?: string;
  ariaLabel?: string;
  animateFrom?: number;
  animationKey?: string;
  animationDurationMs?: number;
  onAnimationComplete?: () => void;
};

const clampPercentage = (value: number) => Math.min(100, Math.max(0, value));

export const ProgressBar = ({
  value,
  label,
  ariaLabel,
  animateFrom,
  animationKey,
  animationDurationMs = 900,
  onAnimationComplete
}: ProgressBarProps) => {
  const finalValue = clampPercentage(value ?? 0);
  const initialValue = animateFrom !== undefined && animateFrom < finalValue ? clampPercentage(animateFrom) : finalValue;
  const [displayValue, setDisplayValue] = useState(initialValue);
  const [animating, setAnimating] = useState(false);
  const [glowing, setGlowing] = useState(false);
  const callbackRef = useRef(onAnimationComplete);
  const completedKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    callbackRef.current = onAnimationComplete;
  }, [onAnimationComplete]);

  useEffect(() => {
    const from = animateFrom === undefined ? undefined : clampPercentage(animateFrom);
    const key = animationKey ?? (from === undefined ? undefined : `${from}:${finalValue}`);
    const shouldAnimate = from !== undefined && from < finalValue && key !== undefined;
    let frame = 0;
    let glowTimer = 0;
    let completionTimer = 0;

    const complete = (withGlow: boolean) => {
      setDisplayValue(finalValue);
      setAnimating(false);
      if (withGlow) {
        setGlowing(true);
        glowTimer = window.setTimeout(() => setGlowing(false), 420);
      }
      if (key && completedKeyRef.current !== key) {
        completedKeyRef.current = key;
        callbackRef.current?.();
      }
    };

    if (!shouldAnimate) {
      frame = window.requestAnimationFrame(() => {
        setDisplayValue(finalValue);
        setAnimating(false);
        setGlowing(false);
      });
      return () => window.cancelAnimationFrame(frame);
    }

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reducedMotion) {
      completionTimer = window.setTimeout(() => complete(false), 0);
      return () => window.clearTimeout(completionTimer);
    }

    frame = window.requestAnimationFrame((startedAt) => {
      setDisplayValue(from);
      setAnimating(true);
      setGlowing(false);
      const tick = (now: number) => {
        const elapsed = Math.min(1, (now - startedAt) / animationDurationMs);
        const eased = 1 - Math.pow(1 - elapsed, 3);
        setDisplayValue(from + (finalValue - from) * eased);
        if (elapsed < 1) frame = window.requestAnimationFrame(tick);
        else complete(true);
      };
      frame = window.requestAnimationFrame(tick);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(glowTimer);
      window.clearTimeout(completionTimer);
    };
  }, [animateFrom, animationDurationMs, animationKey, finalValue]);

  return (
    <div className="progress-wrap">
      {label && <div className="progress-label"><span>{label}</span><strong>{value === undefined ? '—' : `${Math.round(displayValue)}%`}</strong></div>}
      <div className="progress-track" role="progressbar" aria-label={ariaLabel} aria-valuemin={0} aria-valuemax={100} aria-valuenow={value === undefined ? undefined : Math.round(finalValue)}>
        <span className={[animating ? 'is-animating' : '', glowing ? 'is-glowing' : ''].filter(Boolean).join(' ') || undefined} style={{ width: `${displayValue}%` }} />
      </div>
    </div>
  );
};

export const BookCover = ({ book, priority = false, size = 'medium' }: { book: Book; priority?: boolean; size?: 'small' | 'medium' | 'large' }) => {
  const url = useCoverUrl(book.coverAssetId);
  return (
    <div className={`book-cover book-cover--${size}`}>
      {url ? <img src={url} alt={`Capa de ${book.title}`} loading={priority ? 'eager' : 'lazy'} decoding="async" /> : (
        <div className="book-cover__fallback" aria-label={`Sem capa para ${book.title}`}>
          <BookOpen aria-hidden="true" />
          <span>{book.title.slice(0, 1).toUpperCase()}</span>
        </div>
      )}
    </div>
  );
};

export const BookProgress = ({
  book,
  compact = false,
  animateFrom,
  animationKey,
  animationDurationMs,
  onAnimationComplete
}: { book: Book; compact?: boolean } & Pick<ProgressBarProps, 'animateFrom' | 'animationKey' | 'animationDurationMs' | 'onAnimationComplete'>) => {
  const value = progressPercentage(book);
  return <ProgressBar {...(value !== undefined ? { value } : {})} {...(!compact ? { label: 'Progresso' } : {})} {...(animateFrom !== undefined ? { animateFrom } : {})} {...(animationKey ? { animationKey } : {})} {...(animationDurationMs !== undefined ? { animationDurationMs } : {})} {...(onAnimationComplete ? { onAnimationComplete } : {})} />;
};

export const EmptyState = ({ icon, title, children, action }: { icon?: ReactNode; title: string; children: ReactNode; action?: ReactNode }) => (
  <section className="empty-state">
    <div className="empty-state__icon">{icon ?? <BookOpen />}</div>
    <h2>{title}</h2>
    <p>{children}</p>
    {action}
  </section>
);

export const Modal = ({ open, title, onClose, children }: PropsWithChildren<{ open: boolean; title: string; onClose: () => void }>) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!open) {
      if (dialog.open) dialog.close();
      const returnTarget = returnFocusRef.current;
      window.setTimeout(() => returnTarget?.focus(), 0);
      return;
    }
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const initialFocusTarget = () => dialog.querySelector<HTMLElement>('[autofocus]')
      ?? dialog.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled])')
      ?? dialog.querySelector<HTMLElement>('button:not([disabled])');
    if (!dialog.open) {
      dialog.showModal();
      initialFocusTarget()?.focus();
    }
    const focusFrame = requestAnimationFrame(() => {
      if (!dialog.isConnected || dialogRef.current !== dialog || !dialog.open) return;
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && activeElement !== dialog && dialog.contains(activeElement)) return;
      initialFocusTarget()?.focus();
    });
    return () => cancelAnimationFrame(focusFrame);
  }, [open]);
  return (
    <dialog ref={dialogRef} className="modal-dialog" aria-labelledby={titleId} onCancel={(event) => { event.preventDefault(); onClose(); }} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="modal-sheet">
        <header className="modal-sheet__header">
          <h2 id={titleId}>{title}</h2>
          <IconButton label="Fechar" onClick={onClose}><X /></IconButton>
        </header>
        {children}
      </section>
    </dialog>
  );
};

export const StatCard = ({ label, value, detail }: { label: string; value: string | number; detail?: string }) => (
  <article className="stat-card"><span>{label}</span><strong>{value}</strong>{detail && <small>{detail}</small>}</article>
);

export const PageTitle = ({ eyebrow, title, action }: { eyebrow?: string; title: string; action?: ReactNode }) => (
  <header className="page-title">
    <div>{eyebrow && <span>{eyebrow}</span>}<h1>{title}</h1></div>
    {action}
  </header>
);
